import React from 'react';
import { format, isAfter, isSameDay, isBefore, addDays, isValid, subDays, startOfDay } from 'date-fns';
import { CalendarCheck, TrendingUp, CheckCircle, Circle, Trash2, DollarSign, AlertCircle } from 'lucide-react';
import useEvents from '../hooks/useEvents';
import { updateEvent } from '../lib/firebase';
import { CalendarEvent } from '../types';

export default function UpcomingPaydays() {
  const { events, loading, error, deleteEvent } = useEvents();
  const [lastCheck, setLastCheck] = React.useState(new Date());

  // Check for passed paydays every minute
  React.useEffect(() => {
    const checkPaydays = () => {
      if (!events) return;
      
      const now = new Date();
      const today = startOfDay(now);
      
      // Find paydays that have passed since last check
      const passedPaydays = events.filter(event => 
        event.type === 'payday' &&
        !event.isPaid &&
        isValid(event.date) &&
        (isBefore(event.date, today) || isSameDay(event.date, today))
      );

      // Mark passed paydays as paid
      passedPaydays.forEach(async (payday) => {
        await updateEvent({
          ...payday,
          isPaid: true,
          paidDate: now
        });
      });

      setLastCheck(now);
    };

    // Run initial check
    checkPaydays();

    // Set up interval
    const interval = setInterval(checkPaydays, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [events]);

  const calculateRunningBalance = (date: Date): number => {
    if (!events || !isValid(date)) return 0;

    // Find the latest adjustment before or on the given date
    const latestAdjustment = events
      .filter(event => 
        event.type === 'adjustment' && 
        (isBefore(event.date, date) || isSameDay(event.date, date))
      )
      .sort((a, b) => b.date.getTime() - a.date.getTime())[0];

    // If there's an adjustment, only consider transactions after it
    const startDate = latestAdjustment ? latestAdjustment.date : new Date(0);
    const startingBalance = latestAdjustment ? latestAdjustment.amount : 0;

    // Get all relevant transactions
    const relevantTransactions = events
      .filter(event => {
        const eventDate = event.date;
        return (isAfter(eventDate, startDate) || (event === latestAdjustment)) &&
               (isBefore(eventDate, date) || isSameDay(eventDate, date));
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Calculate running balance
    return relevantTransactions.reduce((balance, event) => {
      if (event.type === 'adjustment') {
        return event.amount;
      }
      if (event.type === 'payday') {
        return balance + event.amount;
      }
      if (event.type === 'bill') {
        return balance - event.amount;
      }
      return balance;
    }, startingBalance);
  };

  const getPaydayOverview = () => {
    if (!events) return [];
    
    const today = new Date();
    if (!isValid(today)) return [];
    
    const allPaydays = events
      .filter(event => 
        event.type === 'payday' && 
        isValid(event.date)
      )
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Find the current payday (most recent payday that hasn't been passed by the next one)
    const currentPayday = allPaydays.find(payday => {
      const nextPayday = allPaydays.find(p => isAfter(p.date, payday.date));
      return !nextPayday || isBefore(today, nextPayday.date);
    });

    // Find the next four upcoming paydays after the current one
    const upcomingPaydays = allPaydays
      .filter(payday => 
        currentPayday && isAfter(payday.date, currentPayday.date)
      )
      .slice(0, 4);

    // Find the previous payday (the one before current)
    const previousPayday = currentPayday ? 
      allPaydays.filter(p => isBefore(p.date, currentPayday.date)).pop() : 
      null;

    // Combine all paydays in order
    const paydays = [
      ...(previousPayday ? [previousPayday] : []),
      ...(currentPayday ? [currentPayday] : []),
      ...upcomingPaydays
    ];
    
    return paydays.map((payday, index) => {
      // Get the next payday, if it exists
      const nextPayday = paydays[index + 1];
      
      // Get all bills between this payday and the next payday
      const associatedBills = events
        .filter(event => {
          if (event.type !== 'bill' || !isValid(event.date)) return false;

          const isAfterOrOnPayday = isAfter(event.date, payday.date) || isSameDay(event.date, payday.date);
          
          if (nextPayday) {
            // If there's a next payday, get bills until that payday (exclusive)
            return isAfterOrOnPayday && isBefore(event.date, nextPayday.date);
          } else {
            // If this is the last payday shown, get bills until the next payday in allPaydays
            const nextFuturePayday = allPaydays.find(p => isAfter(p.date, payday.date));
            if (nextFuturePayday) {
              return isAfterOrOnPayday && isBefore(event.date, nextFuturePayday.date);
            }
            // If no future payday exists, use 14 days as fallback
            return isAfterOrOnPayday && isBefore(event.date, addDays(payday.date, 14));
          }
        })
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      // Calculate balance for the day before the next payday
      const balanceDate = nextPayday ? subDays(nextPayday.date, 1) : addDays(payday.date, 14);

      return {
        payday,
        bills: associatedBills,
        runningBalance: calculateRunningBalance(balanceDate),
        nextPaydayDate: nextPayday?.date || addDays(payday.date, 14),
        isPrevious: previousPayday && payday.id === previousPayday.id,
        isCurrent: currentPayday && payday.id === currentPayday.id,
        totalBills: associatedBills.reduce((sum, bill) => sum + bill.amount, 0)
      };
    });
  };

  const handleTogglePaid = async (bill: CalendarEvent) => {
    try {
      await updateEvent({
        ...bill,
        isPaid: !bill.isPaid,
        paidDate: !bill.isPaid ? new Date() : undefined
      });
    } catch (error) {
      console.error('Error updating bill payment status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
        <div className="mt-2 text-red-600">Error: {error.message}</div>
      </div>
    );
  }

  const paydayOverview = getPaydayOverview();

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center">
            <CalendarCheck className="w-6 h-6 mr-2" />
            Upcoming Paydays
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {paydayOverview.map(({ payday, bills, runningBalance, nextPaydayDate, isPrevious, isCurrent, totalBills }) => (
            <div
              key={payday.id}
              className={`bg-white rounded-lg shadow-lg overflow-hidden border ${
                isCurrent ? 'border-green-500 ring-1 ring-green-500' :
                isPrevious ? 'border-gray-200 opacity-75' : 'border-gray-200'
              }`}
            >
              {/* Header */}
              <div className={`px-6 py-4 ${isCurrent ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CalendarCheck className={`w-5 h-5 ${isCurrent ? 'text-green-600' : 'text-gray-500'}`} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{payday.title}</h3>
                      <p className="text-sm text-gray-500">{format(payday.date, 'MMMM d, yyyy')}</p>
                    </div>
                  </div>
                  {(isPrevious || isCurrent) && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isCurrent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {isCurrent ? 'Current' : 'Previous'}
                    </span>
                  )}
                </div>
              </div>

              {/* Amount and Balance */}
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Payday Amount</p>
                    <p className="text-2xl font-bold text-green-600">${payday.amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Running Balance</p>
                    <p className={`text-2xl font-bold ${runningBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${runningBalance.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bills */}
              <div className="px-6 py-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Bills This Period</h4>
                  <span className="text-sm font-medium text-gray-900">
                    Total: ${totalBills.toFixed(2)}
                  </span>
                </div>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {bills.map((bill) => (
                    <div
                      key={bill.id}
                      className="flex items-center justify-between py-1"
                    >
                      <div className="flex items-center">
                        <button
                          onClick={() => handleTogglePaid(bill)}
                          className="mr-2 focus:outline-none"
                        >
                          {bill.isPaid ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Circle className="w-4 h-4 text-gray-300" />
                          )}
                        </button>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{bill.title}</p>
                          <p className="text-xs text-gray-500">Due {format(bill.date, 'MMM d')}</p>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        ${bill.amount.toFixed(2)}
                      </span>
                    </div>
                  ))}

                  {bills.length === 0 && (
                    <div className="text-center py-4">
                      <DollarSign className="mx-auto h-6 w-6 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">No bills scheduled</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 bg-gray-50 text-xs text-gray-500">
                Next payday: {format(nextPaydayDate, 'MMMM d, yyyy')}
              </div>
            </div>
          ))}

          {paydayOverview.length === 0 && (
            <div className="col-span-full text-center py-12 glass-effect rounded-xl hover:scale-105 transition-all duration-300 animate-fadeIn">
              <CalendarCheck className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No paydays found</h3>
              <p className="mt-1 text-gray-500">
                Add some paydays to your calendar to see them here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}